import { gql } from '@apollo/client';
import { debounce, filter, forEach, map, remove } from 'lodash-es';
import { v4 as uuid } from 'uuid';

import type { AuthUser } from 'common/auth/types';

import { loggerClient } from '../graphql/client';

import type { CIAppLog } from './ci.logger';

const sendLogsGQL = gql`
    mutation SendLogs($logs: [InputCreateAppLog!]) {
        logApplicationData(inputs: $logs)
    }
`;

const LOG_STORAGE_KEY = 'ciLogQueue';
const LOG_FLUSH_FREQ = 1000;
const KEEPALIVE_MAX_BYTES = 60_000; // Browsers reject a keepalive request above 64 kB, so a larger backlog goes out as a normal request

function fitsKeepalive(logs: CIAppLog[]): boolean {
    return new TextEncoder().encode(JSON.stringify(logs)).length <= KEEPALIVE_MAX_BYTES;
}

function ignoreSendFailureToAvoidLogLoop(): void {}

function addLogToQueue(log: CIAppLog): void {
    // The key may be missing: JSON.parse(null) returns null, which the checks below rely on (also applies to the reads further down)
    let logStorage = JSON.parse(window.localStorage.getItem(LOG_STORAGE_KEY) as string);
    if (!logStorage) {
        logStorage = [];
    }

    logStorage.push({ id: uuid(), log: log });
    try {
        window.localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(logStorage));
    } catch (error) {
        if (error.name === 'QuotaExceededError') {
            window.localStorage.removeItem(LOG_STORAGE_KEY);
        }
    }
}

export class LogOutbox {
    private lastSendPromise: Promise<void> | null = null;
    private readonly logIdsInFlight = new Set<string>();
    private readonly sendLogs = debounce((): void => {
        let logStorage = JSON.parse(window.localStorage.getItem(LOG_STORAGE_KEY) as string);
        const activeUser = localStorage.getItem('activeUser') as string;
        const userPk = (JSON.parse(activeUser) as AuthUser)?.pk;

        if (!userPk || !logStorage || !logStorage.length) {
            window.localStorage.removeItem(LOG_STORAGE_KEY);
            this.lastSendPromise = Promise.resolve();

            return;
        }

        const unsentLogStorage = filter(logStorage, ({ id }) => !this.logIdsInFlight.has(id));
        if (!unsentLogStorage.length) {
            return;
        }

        const logs = map(unsentLogStorage, 'log');
        const logKeys = map(unsentLogStorage, 'id');
        forEach(logKeys, (key): void => {
            this.logIdsInFlight.add(key);
        });

        const response = loggerClient.mutate({
            mutation: sendLogsGQL,
            variables: {
                logs: logs,
            },
            context: {
                isLoggerClient: true,
                fetchOptions: { keepalive: fitsKeepalive(logs) },
            },
        });

        this.lastSendPromise = Promise.resolve(response)
            .then((): void => {
                logStorage = JSON.parse(window.localStorage.getItem(LOG_STORAGE_KEY) as string);
                forEach(logKeys, (key): void => {
                    // eslint-disable-next-line lodash/prefer-immutable-method
                    remove(logStorage, { id: key });
                });
                window.localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(logStorage));
            }, ignoreSendFailureToAvoidLogLoop)
            .finally((): void => {
                forEach(logKeys, (key): void => {
                    this.logIdsInFlight.delete(key);
                });
            });
    }, LOG_FLUSH_FREQ);

    public enqueue(log: CIAppLog): void {
        addLogToQueue(log);
        this.sendLogs();
    }

    public scheduleSend(): void {
        this.sendLogs();
    }

    public async flush(timeoutMs: number): Promise<void> {
        this.sendLogs();
        this.sendLogs.flush();

        if (this.lastSendPromise === null) {
            return;
        }

        await Promise.race([this.lastSendPromise, new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
    }
}
